
import { useState, useEffect } from "react";
import { UserAvatar } from "@/components/UserAvatar";
import { Button } from "@/components/ui/button";
import { MessageSquare } from "lucide-react";
import { Database } from "@/integrations/supabase/types";
import { useAuth } from "@/contexts/AuthContext";
import MediaMessage from "./MediaMessage";
import MessageActions from "./MessageActions";
import MessageEditor from "./MessageEditor";
import EmojiSticker from "./EmojiSticker";
import MessageStickers from "./MessageStickers";
import ReadReceiptIndicator from "./ReadReceiptIndicator";
import MessagePreview from "./MessagePreview";
import { formatTimeWithTimezone } from "@/utils/timezoneUtils";

type ChatMessage = Database['public']['Tables']['chat_messages']['Row'];

interface ChatMessageProps {
  message: ChatMessage & {
    reply_to?: {
      id: string;
      content: string;
      sender_id: string;
      media_type?: string;
      media_url?: string;
      sent_at: string;
    };
  };
  isCurrentUser: boolean;
  senderName?: string;
  avatarUrl?: string;
  repliedToSenderName?: string;
  onEdit?: (messageId: string, newContent: string) => Promise<boolean>;
  onDelete?: (messageId: string) => Promise<boolean>;
  onReply?: (message: ChatMessage) => void;
  onAddSticker?: (messageId: string, emoji: string) => void;
  onRemoveSticker?: (messageId: string, emoji: string) => void;
  messageStickers?: Array<{
    id: string;
    emoji: string;
    userId: string;
    userName: string;
    count: number;
  }>;
  readReceipts?: Array<{
    messageId: string;
    userId: string;
    readAt: string;
  }>;
  getUserDisplayName?: (userId: string) => string;
  isReply?: boolean;
  canDeleteOthersMessages?: boolean;
}

export default function ChatMessage({ 
  message, 
  isCurrentUser, 
  senderName,
  avatarUrl,
  repliedToSenderName,
  onEdit,
  onDelete,
  onReply,
  onAddSticker,
  onRemoveSticker,
  messageStickers = [],
  readReceipts = [],
  getUserDisplayName,
  isReply = false,
  canDeleteOthersMessages = false
}: ChatMessageProps) {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [showTimestamp, setShowTimestamp] = useState(false);
  // Removed optimistic stickers - relying on real-time database updates only

  const initials = senderName 
    ? senderName.split(' ')
        .filter(n => n.length > 0)
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .substring(0, 2) || '?'
    : '?';

  const timestamp = formatTimeWithTimezone(message.sent_at || new Date().toISOString());

  // Check if this message has stickers from database only
  const hasStickers = messageStickers.length > 0;

  const handleEdit = async (newContent: string) => {
    if (onEdit) {
      const success = await onEdit(message.id, newContent);
      if (success) {
        setIsEditing(false);
      }
    }
  };

  const handleDelete = async () => {
    if (onDelete) {
      await onDelete(message.id);
    }
  };

  const handleReply = () => {
    if (onReply) {
      onReply(message);
    }
  };

  const handleAddSticker = (messageId: string, emoji: string) => {
    console.log("🟢 ChatMessage - handleAddSticker called:", { messageId: messageId?.substring(0, 8), emoji });
    
    // Validate parameters to prevent corruption
    if (!messageId || !emoji) {
      console.error("ChatMessage - Invalid parameters:", { messageId, emoji });
      return;
    }

    // Safety check to prevent messageId/emoji confusion
    if (messageId === emoji) {
      console.error("ChatMessage - MessageId and emoji are identical, preventing corruption");
      return;
    }

    if (onAddSticker && user) {
      console.log("🚀 Calling onAddSticker for database operation");
      onAddSticker(messageId, emoji);
    } else {
      console.error("Missing onAddSticker or user:", { onAddSticker: !!onAddSticker, user: !!user });
    }
  };

  const handleRemoveSticker = (messageId: string, emoji: string) => {
    console.log("🔴 ChatMessage - handleRemoveSticker called:", { messageId: messageId?.substring(0, 8), emoji });
    
    // Validate parameters
    if (!messageId || !emoji) {
      console.error("ChatMessage - Invalid parameters for remove:", { messageId, emoji });
      return;
    }

    if (onRemoveSticker && user) {
      console.log("🚀 Calling onRemoveSticker for database operation");
      onRemoveSticker(messageId, emoji);
    } else {
      console.error("Missing onRemoveSticker or user:", { onRemoveSticker: !!onRemoveSticker, user: !!user });
    }
  };

  // Use only real stickers from database - no optimistic updates
  const allStickers = messageStickers;

  // If message has media, display as a single Discord-like message with media content
  if (message.media_type && message.media_url) {
    return (
      <div className={`flex gap-3 ${isCurrentUser ? 'flex-row-reverse' : 'flex-row'} mb-4 group`}>
      <div className="flex-shrink-0">
        <UserAvatar
          userId={message.sender_id || undefined}
          avatarUrl={avatarUrl}
          fallback={initials}
          size="sm"
          className="ring-2 ring-anyskill-purple/20"
          showLevel={true}
        />
      </div>
        
        <div className={`flex flex-col ${isReply ? 'max-w-[90%]' : 'max-w-[70%]'} ${isCurrentUser ? 'items-end' : 'items-start'} relative`}>
          {/* Reply Preview */}
          {message.reply_to && (
            <MessagePreview
              message={message}
              senderName={senderName}
              repliedToSenderName={repliedToSenderName}
              isCurrentUser={isCurrentUser}
            />
          )}

          {/* Message Actions */}
          <div className={`absolute -top-2 ${isCurrentUser ? '-left-8' : '-right-8'} opacity-0 group-hover:opacity-100 transition-opacity z-10`}>
          <MessageActions
            messageId={message.id}
            onEdit={() => {}} // Media messages don't support text editing
            onDelete={handleDelete}
            onReply={handleReply}
            onAddSticker={handleAddSticker}
            disabled={!user}
            showReplyAndSticker={true}
            isCurrentUser={isCurrentUser}
            canDeleteOthersMessages={canDeleteOthersMessages}
          />
          </div>

          {!isCurrentUser && senderName && (
            <span className="text-xs text-muted-foreground mb-1 px-2">
              {senderName}
            </span>
          )}
          
          {/* Media Content */}
          <div className={`
            rounded-2xl overflow-hidden shadow-lg relative cursor-pointer
            ${hasStickers ? 'mb-6' : ''} 
            ${isCurrentUser 
              ? 'bg-gradient-to-r from-anyskill-purple to-anyskill-blue' 
              : 'bg-muted'
            }
          `}
          onClick={() => setShowTimestamp(!showTimestamp)}>
            <MediaMessage
              url={message.media_url!}
              type={message.media_type as 'image' | 'video' | 'gif'}
              filename={message.media_filename!}
              size={message.media_size || 0}
            />
            
            {/* Stickers */}
            <MessageStickers
              messageId={message.id}
              stickers={allStickers}
              currentUserId={user?.id}
              onAddSticker={handleAddSticker}
              onRemoveSticker={handleRemoveSticker}
              isMediaMessage={true}
            />
          </div>

          {/* Text content if it exists */}
          {message.content && message.content.trim() && (
            <div className={`
              px-3 py-2 rounded-xl text-sm break-words mt-2
              ${isCurrentUser 
                ? 'bg-gradient-to-r from-anyskill-purple/80 to-anyskill-blue/80 text-white' 
                : 'bg-muted/70 text-foreground'
              }
            `}>
              {message.content}
              {message.edited_at && (
                <span className="text-xs opacity-70 ml-2">(edited)</span>
              )}
            </div>
          )}
          
          {/* Timestamp */}
          {showTimestamp && (
            <span className="text-xs text-muted-foreground mt-1 px-2 animate-fade-in">
              {timestamp}
            </span>
          )}
        </div>
      </div>
    );
  }

  // Regular text message
  return (
    <div className={`flex gap-3 ${isCurrentUser ? 'flex-row-reverse' : 'flex-row'} mb-4 group`}>
    <div className="flex-shrink-0">
      <UserAvatar
        userId={message.sender_id || undefined}
        avatarUrl={avatarUrl}
        fallback={initials}
        size="sm"
        className="ring-2 ring-anyskill-purple/20"
        showLevel={true}
      />
    </div>
      
      <div className={`flex flex-col max-w-[70%] ${isCurrentUser ? 'items-end' : 'items-start'} relative`}>
        {/* Reply Preview */}
        {message.reply_to && (
          <MessagePreview
            message={message}
            senderName={senderName}
            repliedToSenderName={repliedToSenderName}
            isCurrentUser={isCurrentUser}
          />
        )}

        {/* Message Actions - Top Right */}
        <div className={`absolute -top-2 ${isCurrentUser ? '-left-8' : '-right-8'} opacity-0 group-hover:opacity-100 transition-opacity z-10`}>
          <MessageActions
            messageId={message.id}
            onEdit={() => setIsEditing(true)}
            onDelete={handleDelete}
            onReply={handleReply}
            onAddSticker={handleAddSticker}
            disabled={!user}
            showReplyAndSticker={true}
            isCurrentUser={isCurrentUser}
            canDeleteOthersMessages={canDeleteOthersMessages}
          />
        </div>

        {!isCurrentUser && senderName && (
          <span className="text-xs text-muted-foreground mb-1 px-2">
            {senderName}
          </span>
        )}
        
        {isEditing ? (
          <div className="w-full min-w-[200px]">
            <MessageEditor
              initialContent={message.content || ''}
              onSave={handleEdit}
              onCancel={() => setIsEditing(false)}
            />
          </div>
        ) : (
          <>
            <div
              className={`
                px-4 py-2 rounded-2xl text-sm break-words relative cursor-pointer transition-all
                ${hasStickers ? 'mb-6' : ''} 
                ${isCurrentUser 
                  ? 'bg-gradient-to-r from-anyskill-purple to-anyskill-blue text-white' 
                  : 'bg-muted dark:bg-slate-700 text-foreground dark:text-slate-200'
                }
              `}
              onClick={() => setShowTimestamp(!showTimestamp)}
            >
              {message.content}
              {message.edited_at && (
                <span className="text-xs opacity-70 ml-2">(edited)</span>
              )}
              
              {/* Stickers positioned at bottom using freed space */}
              <MessageStickers
                messageId={message.id}
                stickers={allStickers}
                currentUserId={user?.id}
                onAddSticker={handleAddSticker}
                onRemoveSticker={handleRemoveSticker}
              />
            </div>
            
            {/* Hidden timestamp, revealed on gesture */}
            {showTimestamp && (
              <span className="text-xs text-muted-foreground mt-1 px-2 animate-fade-in">
                {timestamp}
              </span>
            )}
            
            {/* Read Receipt Indicator */}
            <ReadReceiptIndicator
              readReceipts={readReceipts}
              isCurrentUser={isCurrentUser}
              getUserDisplayName={getUserDisplayName}
            />
          </>
        )}
      </div>
    </div>
  );
}
